import {useEffect} from 'react';
import company from '../config/company';

const DEFAULT_IMAGE = `${company.url}/og-image.jpg`;

const upsert = (selector, create, apply) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
  return el;
};

const setMeta = (attr, key, content) => {
  if (content === undefined || content === null) return;
  upsert(
    `meta[${attr}="${key}"]`,
    () => {
      const m = document.createElement('meta');
      m.setAttribute(attr, key);
      return m;
    },
    (m) => m.setAttribute('content', content)
  );
};

/**
 * Setzt Titel, Beschreibung, Canonical, Open-Graph-Tags und optional
 * strukturierte Daten (JSON-LD) für die aktuelle Seite. Die Werte landen
 * durch das Prerendering auch im ausgelieferten HTML.
 */
export default function usePageMeta({title, description, path = '/', image, jsonLd, noindex = false}) {
  useEffect(() => {
    const fullTitle = title || `${company.brandName} | ${company.tagline}`;
    const url = `${company.url}${path === '/' ? '/' : path}`;
    const img = image || DEFAULT_IMAGE;

    document.title = fullTitle;
    document.documentElement.setAttribute('lang', 'de');

    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', `${company.brandName} Pflegedienst`);
    setMeta('property', 'og:locale', 'de_DE');
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', img);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', img);

    upsert(
      'link[rel="canonical"]',
      () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        return l;
      },
      (l) => l.setAttribute('href', url)
    );

    const existing = document.head.querySelector('script[data-page-jsonld]');
    if (existing) existing.remove();
    if (jsonLd) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-page-jsonld', 'true');
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, path, image, jsonLd, noindex]);
}

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${company.url}${item.path}`
  }))
});
